import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer
} from '@mui/material';
import { formatCurrency, formatPercentage } from '../../../utils/dashboardAnalyticsEngine';

const SUBTITLES_MAP = {
    doc1: "Analyse globale par activité",
    doc2: "Analyse chiffre d'affaires et marge par activité",
    doc3: "Analyse marge par compagnie",
    doc4: "Analyse tourisme par prestation",
    doc5: "Analyse tourisme par client",
    graphique: "Analyse graphique"
};

const DashboardDocumentViewer = ({
    analysisId = 'doc1',
    analysisTitle = 'Analyse globale par activité',
    periods = [],
    periodData = [],
    allCompagnies = [],
    allClients = [],
    criteria = {},
    onRegisterExportGetter = null
}) => {
    const [activeRowIdx, setActiveRowIdx] = useState(0);
    const tableRef = useRef(null);

    // Date et heure de tirage actuelles
    const dateTirage = useMemo(() => {
        const now = new Date();
        const d = String(now.getDate()).padStart(2, '0');
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const y = String(now.getFullYear()).slice(-2);
        const hh = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        return `${d}/${m}/${y} à ${hh}:${mm}`;
    }, []);

    // Période globale affichée en haut à droite
    const periodeGlobale = useMemo(() => {
        if (!periods || periods.length === 0) return { start: '--', end: '--' };
        const pFirst = periods[0]?.start;
        const pLast = periods[periods.length - 1]?.end;
        return {
            start: pFirst ? pFirst.format('DD/MM/YYYY') : criteria.startDate || '--',
            end: pLast ? pLast.format('DD/MM/YYYY') : criteria.endDate || '--'
        };
    }, [periods, criteria]);

    // Construction des lignes tabulaires selon le document sélectionné
    const rows = useMemo(() => {
        if (!periodData || periodData.length === 0) return [];

        const generatedRows = [];

        // ═════════════════════════════════════════════════════════════════════
        // DOC 1 — Analyse globale par activité
        // ═════════════════════════════════════════════════════════════════════
        if (analysisId === 'doc1') {
            // Section Billetterie
            generatedRows.push({ isSectionHeader: true, label: 'Billetterie' });
            generatedRows.push({
                label: 'Chiffre d\'affaires',
                formula: 'a',
                values: periodData.map(p => formatCurrency(p.billetterie.ca, p.isEmpty))
            });
            generatedRows.push({
                label: 'Achats billets d\'avions',
                formula: 'b',
                values: periodData.map(p => formatCurrency(p.billetterie.achats, p.isEmpty))
            });
            generatedRows.push({
                label: 'Marge billetterie',
                formula: 'c = a-b',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.billetterie.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '% marge/C.A',
                formula: '= c/a',
                isPercent: true,
                values: periodData.map(p => formatPercentage(p.billetterie.pctMarge, p.isEmpty))
            });

            // Section Tourisme
            generatedRows.push({ isSectionHeader: true, label: 'Tourisme' });
            generatedRows.push({
                label: 'Chiffre d\'affaires',
                formula: 'd',
                values: periodData.map(p => formatCurrency(p.tourisme.ca, p.isEmpty))
            });
            generatedRows.push({
                label: 'Débours',
                formula: 'e',
                values: periodData.map(p => formatCurrency(p.tourisme.debours, p.isEmpty))
            });
            generatedRows.push({
                label: 'Marge tourisme',
                formula: 'f = d-e',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.tourisme.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '% marge/C.A',
                formula: '= f/d',
                isPercent: true,
                values: periodData.map(p => formatPercentage(p.tourisme.pctMarge, p.isEmpty))
            });

            // Section Global
            generatedRows.push({ isSectionHeader: true, label: 'Global' });
            generatedRows.push({
                label: 'Chiffre d\'affaires',
                formula: 'g = a+d',
                values: periodData.map(p => formatCurrency(p.global.ca, p.isEmpty))
            });
            generatedRows.push({
                label: 'Débours',
                formula: 'h = b+e',
                values: periodData.map(p => formatCurrency(p.global.debours, p.isEmpty))
            });
            generatedRows.push({
                label: 'Marge globale',
                formula: 'i = c+f',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.global.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '% marge/C.A',
                formula: '= i/g',
                isPercent: true,
                values: periodData.map(p => formatPercentage(p.global.pctMarge, p.isEmpty))
            });
        }

        // ═════════════════════════════════════════════════════════════════════
        // DOC 2 — Analyse Chiffre d'affaires et marges par activité
        // ═════════════════════════════════════════════════════════════════════
        else if (analysisId === 'doc2') {
            // Chiffre d'affaires global
            generatedRows.push({
                label: 'Chiffre d\'affaires global',
                formula: 'a (Total C.A Billetterie + Tourisme)',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.global.ca, p.isEmpty))
            });
            generatedRows.push({
                label: '%',
                formula: '= a/a',
                isIndent: true,
                values: periodData.map(p => p.isEmpty ? '' : '100.00 %')
            });
            generatedRows.push({
                label: 'Billetterie',
                formula: 'b',
                values: periodData.map(p => formatCurrency(p.billetterie.ca, p.isEmpty))
            });
            generatedRows.push({
                label: '%',
                formula: '= b/a',
                isIndent: true,
                values: periodData.map(p => formatPercentage(p.global.ca > 0 ? (p.billetterie.ca / p.global.ca) * 100 : 0, p.isEmpty))
            });
            generatedRows.push({
                label: 'Tourisme',
                formula: 'c',
                values: periodData.map(p => formatCurrency(p.tourisme.ca, p.isEmpty))
            });
            generatedRows.push({
                label: '%',
                formula: '= c/a',
                isIndent: true,
                values: periodData.map(p => formatPercentage(p.global.ca > 0 ? (p.tourisme.ca / p.global.ca) * 100 : 0, p.isEmpty))
            });

            // Marge globale
            generatedRows.push({ isSectionHeader: true, label: 'Marges' });
            generatedRows.push({
                label: 'Marge globale',
                formula: 'e (= Marge Billetterie + Tourisme)',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.global.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '%',
                formula: '= e/e',
                isIndent: true,
                values: periodData.map(p => p.isEmpty ? '' : '100.00 %')
            });
            generatedRows.push({
                label: 'Billetterie',
                formula: 'f',
                values: periodData.map(p => formatCurrency(p.billetterie.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '%',
                formula: '= f/e',
                isIndent: true,
                values: periodData.map(p => formatPercentage(p.global.marge > 0 ? (p.billetterie.marge / p.global.marge) * 100 : 0, p.isEmpty))
            });
            generatedRows.push({
                label: 'Tourisme',
                formula: 'g',
                values: periodData.map(p => formatCurrency(p.tourisme.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '%',
                formula: '= g/e',
                isIndent: true,
                values: periodData.map(p => formatPercentage(p.global.marge > 0 ? (p.tourisme.marge / p.global.marge) * 100 : 0, p.isEmpty))
            });
        }

        // ═════════════════════════════════════════════════════════════════════
        // DOC 3 — Analyse billetterie par compagnie
        // ═════════════════════════════════════════════════════════════════════
        else if (analysisId === 'doc3') {
            generatedRows.push({ isSectionHeader: true, label: 'Billetterie' });
            generatedRows.push({
                label: 'Chiffre d\'affaires global',
                formula: 'a',
                values: periodData.map(p => formatCurrency(p.billetterie.ca, p.isEmpty))
            });
            generatedRows.push({
                label: 'Achats billets d\'avions',
                formula: 'b',
                values: periodData.map(p => formatCurrency(p.billetterie.achats, p.isEmpty))
            });
            generatedRows.push({
                label: 'Marges',
                formula: 'c = a-b',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.billetterie.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '% marges',
                formula: '= c/a',
                isPercent: true,
                values: periodData.map(p => formatPercentage(p.billetterie.pctMarge, p.isEmpty))
            });

            // Détail par compagnie dynamique
            generatedRows.push({ isSectionHeader: true, label: 'Par compagnie' });
            const compagniesToDisplay = allCompagnies.length > 0 ? allCompagnies : ['Air Sénégal', 'Air France', 'RAM', 'Corsair', 'Autres'];
            
            compagniesToDisplay.forEach((comp, cIdx) => {
                const formulaLetter = String.fromCharCode(100 + (cIdx % 22)); // d, e, f, ...
                generatedRows.push({
                    label: `${comp}  Ventes`,
                    formula: formulaLetter,
                    values: periodData.map(p => formatCurrency(p.compagnies?.[comp] || 0, p.isEmpty))
                });
                generatedRows.push({
                    label: '%',
                    formula: `= ${formulaLetter}/a`,
                    isIndent: true,
                    values: periodData.map(p => {
                        const totalBillet = p.billetterie.ca;
                        const compVal = p.compagnies?.[comp] || 0;
                        return formatPercentage(totalBillet > 0 ? (compVal / totalBillet) * 100 : 0, p.isEmpty);
                    })
                });
            });
        }

        // ═════════════════════════════════════════════════════════════════════
        // DOC 4 — Analyse tourisme par prestation
        // ═════════════════════════════════════════════════════════════════════
        else if (analysisId === 'doc4') {
            generatedRows.push({ isSectionHeader: true, label: 'Tourisme' });
            generatedRows.push({
                label: 'Chiffre d\'affaires global',
                formula: 'a',
                values: periodData.map(p => formatCurrency(p.tourisme.ca, p.isEmpty))
            });
            generatedRows.push({
                label: 'Débours',
                formula: 'b',
                values: periodData.map(p => formatCurrency(p.tourisme.debours, p.isEmpty))
            });
            generatedRows.push({
                label: 'Marges',
                formula: 'c = a-b',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.tourisme.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '% marges',
                formula: '= c/a',
                isPercent: true,
                values: periodData.map(p => formatPercentage(p.tourisme.pctMarge, p.isEmpty))
            });

            // Détail par prestation (Circuits, Excursions, Transferts, Séjours)
            const prestationDefs = [
                { key: 'circuits', title: '* Circuits *', fCA: 'd', fDeb: 'e', fMarge: 'f = d - e', fPct: '= f/d' },
                { key: 'excursions', title: '* Excursions *', fCA: 'g', fDeb: 'h', fMarge: 'i = g - h', fPct: '= i/g' },
                { key: 'transferts', title: '* Transferts *', fCA: 'j', fDeb: 'k', fMarge: 'l = j - k', fPct: '= l/j' },
                { key: 'sejours', title: '* Séjours *', fCA: 'm', fDeb: 'n', fMarge: 'o = m - n', fPct: '= o/m' }
            ];

            generatedRows.push({ isSectionHeader: true, label: 'Par prestations' });

            prestationDefs.forEach(pDef => {
                generatedRows.push({ isSubSectionHeader: true, label: pDef.title });
                generatedRows.push({
                    label: 'Chiffre d\'affaires',
                    formula: pDef.fCA,
                    values: periodData.map(p => formatCurrency(p.prestations?.[pDef.key]?.ca || 0, p.isEmpty))
                });
                generatedRows.push({
                    label: 'Débours',
                    formula: pDef.fDeb,
                    values: periodData.map(p => formatCurrency(p.prestations?.[pDef.key]?.debours || 0, p.isEmpty))
                });
                generatedRows.push({
                    label: 'Marges',
                    formula: pDef.fMarge,
                    isBold: true,
                    values: periodData.map(p => formatCurrency(p.prestations?.[pDef.key]?.marge || 0, p.isEmpty))
                });
                generatedRows.push({
                    label: '% marges',
                    formula: pDef.fPct,
                    isPercent: true,
                    values: periodData.map(p => formatPercentage(p.prestations?.[pDef.key]?.pctMarge || 0, p.isEmpty))
                });
            });
        }

        // ═════════════════════════════════════════════════════════════════════
        // DOC 5 — Analyse tourisme par client
        // ═════════════════════════════════════════════════════════════════════
        else if (analysisId === 'doc5') {
            generatedRows.push({ isSectionHeader: true, label: 'Tourisme' });
            generatedRows.push({
                label: 'Chiffre d\'affaires global',
                formula: 'a',
                values: periodData.map(p => formatCurrency(p.tourisme.ca, p.isEmpty))
            });
            generatedRows.push({
                label: 'Débours tourisme',
                formula: 'b',
                values: periodData.map(p => formatCurrency(p.tourisme.debours, p.isEmpty))
            });
            generatedRows.push({
                label: 'Marges',
                formula: 'c = a-b',
                isBold: true,
                values: periodData.map(p => formatCurrency(p.tourisme.marge, p.isEmpty))
            });
            generatedRows.push({
                label: '% marges',
                formula: '= c/a',
                isPercent: true,
                values: periodData.map(p => formatPercentage(p.tourisme.pctMarge, p.isEmpty))
            });

            // Détail par client dynamique
            generatedRows.push({ isSectionHeader: true, label: 'Par client' });
            const clientsToDisplay = allClients.length > 0 ? allClients : ['Client A', 'Client B', 'Client C', 'Autres clients'];

            clientsToDisplay.forEach((cli, cIdx) => {
                const formulaLetter = String.fromCharCode(100 + (cIdx % 22)); // d, e, f, ...
                generatedRows.push({
                    label: `${cli}  Ventes`,
                    formula: formulaLetter,
                    values: periodData.map(p => formatCurrency(p.clients?.[cli] || 0, p.isEmpty))
                });
                generatedRows.push({
                    label: '%',
                    formula: `= ${formulaLetter}/a`,
                    isIndent: true,
                    values: periodData.map(p => {
                        const totalTourisme = p.tourisme.ca;
                        const cliVal = p.clients?.[cli] || 0;
                        return formatPercentage(totalTourisme > 0 ? (cliVal / totalTourisme) * 100 : 0, p.isEmpty);
                    })
                });
            });
        }

        return generatedRows;
    }, [analysisId, periodData, allCompagnies, allClients]);

    // Enregistrement de l'export Excel dans le parent
    useEffect(() => {
        if (onRegisterExportGetter) {
            onRegisterExportGetter(() => ({
                title: analysisTitle,
                periods,
                rows
            }));
        }
    }, [onRegisterExportGetter, analysisTitle, periods, rows]);

    // Navigation globale au clavier dans les lignes du tableau
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // Ignorer si l'utilisateur est dans un champ de saisie
            const activeEl = document.activeElement;
            const isInputFocused = activeEl && (
                activeEl.tagName === 'INPUT' ||
                activeEl.tagName === 'TEXTAREA' ||
                activeEl.tagName === 'SELECT' ||
                activeEl.isContentEditable
            );
            if (isInputFocused) return;

            if (!rows || rows.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveRowIdx(prev => Math.min(rows.length - 1, prev + 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveRowIdx(prev => Math.max(0, prev - 1));
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [rows]);

    // Défilement automatique vers la ligne active
    useEffect(() => {
        const rowEl = document.getElementById(`dashboard-doc-row-${activeRowIdx}`);
        if (rowEl) {
            rowEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [activeRowIdx]);

    return (
        <Paper 
            elevation={0}
            ref={tableRef}
            className="printable-document"
            sx={{
                p: { xs: 2, md: 3 },
                bgcolor: 'white',
                border: '1px solid #cbd5e1',
                borderRadius: '16px',
                boxShadow: '0 4px 20px -2px rgba(0,0,0,0.04)',
                outline: 'none'
            }}
        >
            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* EN-TÊTE OFFICIEL DE LA SYNTHÈSE (CONFORME NOTE TECHNIQUE)         */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <Box sx={{ 
                border: '2px solid #0f172a', 
                borderRadius: '12px 12px 0 0', 
                overflow: 'hidden', 
                mb: 0 
            }}>
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 2fr 1fr', 
                    borderBottom: '1px solid #0f172a',
                    p: 1.5,
                    alignItems: 'center',
                    bgcolor: '#f8fafc'
                }}>
                    {/* Gauche : Société & Version */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#0f172a' }}>
                            AFRICA FOR TOURISM
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            TravelManager v2.0
                        </Typography>
                    </Box>

                    {/* Centre : Titre principal & Sous-titre */}
                    <Box textAlign="center">
                        <Typography variant="subtitle1" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#0f172a' }}>
                            Synthèse d'activité
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#2563eb' }}>
                            {SUBTITLES_MAP[analysisId] || analysisTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Date de tirage : {dateTirage}
                        </Typography>
                    </Box>

                    {/* Droite : Période et Page */}
                    <Box textAlign="right">
                        <Typography variant="caption" component="div">
                            <strong>Période du :</strong> {periodeGlobale.start}
                        </Typography>
                        <Typography variant="caption" component="div">
                            <strong>au :</strong> {periodeGlobale.end}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, color: '#64748b' }}>
                            <strong>Page :</strong> 1 / 1
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* TABLEAU DE COMPARAISON SUR LES 4 PÉRIODES AVEC EN-TÊTE FIGÉ       */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <TableContainer 
                sx={{ 
                    maxHeight: '65vh', 
                    overflowX: 'auto',
                    overflowY: 'auto',
                    border: '2px solid #0f172a', 
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#94a3b8 #f1f5f9',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '10px'
                    },
                    '&::-webkit-scrollbar-track': {
                        bgcolor: '#f1f5f9',
                        borderRadius: '6px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#94a3b8',
                        borderRadius: '6px',
                        border: '2px solid #f1f5f9',
                        '&:hover': {
                            bgcolor: '#64748b'
                        }
                    }
                }}
            >
                <Table size="small" stickyHeader sx={{ minWidth: 1050, tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '32%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '13%' }} />
                    </colgroup>

                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ 
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                fontWeight: 800, 
                                bgcolor: '#0f172a', 
                                color: 'white', 
                                fontSize: '0.78rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.6,
                                py: 1.3,
                                px: 2,
                                borderRight: '1px solid rgba(255,255,255,0.25)',
                                borderBottom: '2px solid #334155'
                            }}>
                                Intitulé
                            </TableCell>
                            <TableCell align="center" sx={{ 
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                fontWeight: 800, 
                                bgcolor: '#0f172a', 
                                color: '#f59e0b', 
                                fontSize: '0.78rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.6,
                                py: 1.3,
                                px: 2,
                                borderRight: '1px solid rgba(255,255,255,0.25)',
                                borderBottom: '2px solid #334155'
                            }}>
                                Formules
                            </TableCell>
                            {periods.map((p, idx) => (
                                <TableCell 
                                    key={idx} 
                                    align="right" 
                                    sx={{ 
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                        fontWeight: 800, 
                                        bgcolor: '#0f172a', 
                                        color: 'white', 
                                        fontSize: '0.78rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        py: 1.3,
                                        px: 2,
                                        borderRight: idx < 3 ? '1px solid rgba(255,255,255,0.25)' : 'none',
                                        borderBottom: '2px solid #334155',
                                        whiteSpace: 'pre-line',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {p.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row, idx) => {
                            const isActive = activeRowIdx === idx;

                            // 1. Ligne d'en-tête de section majeure (ex: Billetterie, Tourisme, Global)
                            if (row.isSectionHeader) {
                                return (
                                    <TableRow 
                                        id={`dashboard-doc-row-${idx}`}
                                        key={idx}
                                        onClick={() => setActiveRowIdx(idx)}
                                        sx={{ 
                                            bgcolor: '#0f172a !important',
                                            cursor: 'pointer',
                                            borderBottom: '2px solid #0f172a'
                                        }}
                                    >
                                        <TableCell colSpan={6} sx={{ 
                                            fontWeight: 900, 
                                            color: '#ffffff', 
                                            py: 1,
                                            px: 2,
                                            fontSize: '0.85rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.8,
                                            borderRight: '1px solid #334155'
                                        }}>
                                            ✦ {row.label}
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            // 2. Ligne de sous-section (ex: * Circuits *, * Excursions *)
                            if (row.isSubSectionHeader) {
                                return (
                                    <TableRow 
                                        id={`dashboard-doc-row-${idx}`}
                                        key={idx}
                                        onClick={() => setActiveRowIdx(idx)}
                                        sx={{ 
                                            bgcolor: '#f1f5f9 !important', 
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #e2e8f0'
                                        }}
                                    >
                                        <TableCell colSpan={6} sx={{ 
                                            fontWeight: 700, 
                                            color: '#2563eb', 
                                            py: 0.75,
                                            pl: 3,
                                            fontSize: '0.82rem',
                                            fontStyle: 'italic',
                                            borderRight: '1px solid #cbd5e1'
                                        }}>
                                            {row.label}
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            // 3. Ligne normale de données
                            return (
                                <TableRow
                                    id={`dashboard-doc-row-${idx}`}
                                    key={idx}
                                    onClick={() => setActiveRowIdx(idx)}
                                    onMouseEnter={() => setActiveRowIdx(idx)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: isActive 
                                            ? 'rgba(37, 99, 235, 0.09) !important' 
                                            : (idx % 2 === 0 ? 'white' : '#fcfdfe'),
                                        borderLeft: isActive 
                                            ? '4px solid #2563eb !important' 
                                            : '4px solid transparent',
                                        transition: 'all 0.12s ease-in-out',
                                        '&:hover': { 
                                            bgcolor: isActive 
                                                ? 'rgba(37, 99, 235, 0.14) !important' 
                                                : 'rgba(241, 245, 249, 0.9) !important'
                                        }
                                    }}
                                >
                                    {/* Intitulé */}
                                    <TableCell sx={{ 
                                        pl: row.isIndent ? 4 : 2, 
                                        fontWeight: row.isBold ? 800 : (row.isIndent ? 500 : 600),
                                        color: row.isIndent ? '#64748b' : '#0f172a',
                                        borderRight: '1px solid #cbd5e1',
                                        borderBottom: '1px solid #e2e8f0',
                                        py: 0.8
                                    }}>
                                        {row.label}
                                    </TableCell>

                                    {/* Formule */}
                                    <TableCell align="center" sx={{ 
                                        borderRight: '1px solid #cbd5e1',
                                        borderBottom: '1px solid #e2e8f0',
                                        py: 0.8
                                    }}>
                                        {row.formula ? (
                                            <span style={{ 
                                                fontFamily: 'monospace', 
                                                color: '#b91c1c', 
                                                fontWeight: 700, 
                                                fontSize: '0.78rem',
                                                backgroundColor: '#fee2e2',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                border: '1px solid #fecaca',
                                                display: 'inline-block'
                                            }}>
                                                {row.formula}
                                            </span>
                                        ) : '-'}
                                    </TableCell>

                                    {/* Valeurs sur les 4 périodes */}
                                    {row.values.map((val, vIdx) => (
                                        <TableCell 
                                            key={vIdx} 
                                            align="right" 
                                            sx={{ 
                                                fontWeight: row.isBold ? 800 : (row.isPercent ? 700 : 500),
                                                color: row.isPercent ? '#2563eb' : (row.isBold ? '#0f172a' : '#334155'),
                                                borderRight: vIdx < 3 ? '1px solid #cbd5e1' : 'none',
                                                borderBottom: '1px solid #e2e8f0',
                                                fontVariantNumeric: 'tabular-nums',
                                                fontSize: '0.85rem',
                                                py: 0.8,
                                                px: 2
                                            }}
                                        >
                                            {val}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Aide à la navigation clavier & défilement horizontal */}
            <Box className="no-print" sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, bgcolor: '#f8fafc', p: 1, px: 2, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981' }} />
                    <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Raccourcis clavier :
                        <kbd style={{ backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '1px 5px', fontSize: '0.72rem', fontWeight: 700 }}>↑</kbd>
                        <kbd style={{ backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '1px 5px', fontSize: '0.72rem', fontWeight: 700 }}>↓</kbd>
                        Naviguer entre les lignes
                    </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                    Défilement horizontal disponible (Shift + Molette de la souris)
                </Typography>
            </Box>
        </Paper>
    );
};

export default DashboardDocumentViewer;

